import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { guestApi, getGuestSession } from '../../utils/portalApi';
import { SERVER_URL } from '../../utils/api';

/**
 * The guest's notifications (guest role, Phase 4): new messages in their
 * groups, changes to their tasks. Pushed live over the guest's own socket, and
 * re-read every minute in case the connection dropped.
 *
 * Also the one place the home page hears "you have been switched off" live,
 * so it can sign them out at once rather than at its next request.
 */
const GuestBell = ({ onDisabled, onActivity }) => {
    const navigate = useNavigate();
    const [data, setData] = useState({ unread: 0, items: [] });
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const activityRef = useRef(onActivity);
    activityRef.current = onActivity;
    const disabledRef = useRef(onDisabled);
    disabledRef.current = onDisabled;

    const load = useCallback(() => {
        guestApi.get('/notifications').then(({ data: body }) => setData(body)).catch(() => { });
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, 60000);
        const session = getGuestSession();
        const socket = session ? io(SERVER_URL || window.location.origin, {
            auth: { token: session }, transports: ['websocket', 'polling']
        }) : null;
        socket?.on('guest:notification', () => { load(); activityRef.current?.(); });
        socket?.on('guest:disabled', () => disabledRef.current?.('Your guest access has been switched off.'));
        return () => { clearInterval(timer); socket?.disconnect(); };
    }, [load]);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
    }, [open]);

    const openOne = (n) => {
        setOpen(false);
        if (!n.read) guestApi.post('/notifications/read', { ids: [n._id] }).then(load).catch(() => { });
        if (n.link) navigate(n.link);
    };

    const readAll = () => guestApi.post('/notifications/read', {}).then(load).catch(() => { });

    return (
        <div className="guest-bell" ref={wrapRef}>
            <button type="button" className="guest-bell-btn" aria-label={`Notifications${data.unread ? `, ${data.unread} unread` : ''}`}
                aria-expanded={open} onClick={() => setOpen(!open)}>
                <FontAwesomeIcon icon={faBell} />
                {data.unread > 0 && <span className="guest-unread">{data.unread > 99 ? '99+' : data.unread}</span>}
            </button>
            {open && (
                <div className="guest-bell-panel" role="dialog" aria-label="Notifications">
                    <div className="guest-bell-head">
                        <strong>Notifications</strong>
                        {data.unread > 0 && <button type="button" className="guest-link" onClick={readAll}>Mark all read</button>}
                    </div>
                    {!data.items.length && <p className="guest-muted">Nothing yet.</p>}
                    <ul>
                        {data.items.map((n) => (
                            <li key={n._id}>
                                <button type="button" className={n.read ? '' : 'unread'} onClick={() => openOne(n)}>
                                    <strong>{n.title}{n.count > 1 ? ` (${n.count})` : ''}</strong>
                                    <span>{n.message}</span>
                                    <small>{new Date(n.updatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</small>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default GuestBell;
